import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const useQuickAddNavigation = (params: Params) => {
  const {
    baseProductLineageKey,
    productSlug,
    restaurantSlug,
    selectedDeliveryAddressId,
    sharedLineItemId,
  } = params;

  const router = useRouter();
  const { track } = useTelemetry();

  // ─── Handlers ────────────────────────────────────────────────────────

  const navigateToLocationSearch = useCallback(() => {
    track('quick-add.change-location-tapped', { baseProductLineageKey, sharedLineItemId });

    router.navigate({
      pathname: '/quick-add/[baseProductLineageKey]/search-location',
      params: { baseProductLineageKey },
    });
  }, [baseProductLineageKey, router, sharedLineItemId, track]);

  const navigateToCustomization = useCallback(() => {
    if (!productSlug || !restaurantSlug) {
      navigateToLocationSearch();

      return;
    }

    track('quick-add.customize-tapped', { baseProductLineageKey, sharedLineItemId });

    const sharedLineItemParams = sharedLineItemId ? { sharedLineItemId } : {};
    const customizationParams = { ...sharedLineItemParams, unavailableIngModal: 'false' };

    if (selectedDeliveryAddressId) {
      router.replace({
        pathname: '/delivery/[addressId]/[productSlug]',
        params: { addressId: selectedDeliveryAddressId, productSlug, ...customizationParams },
      });

      return;
    }

    router.replace({
      pathname: '/[restaurantSlug]/[productSlug]',
      params: { restaurantSlug, productSlug, ...customizationParams },
    });
  }, [
    baseProductLineageKey,
    navigateToLocationSearch,
    productSlug,
    restaurantSlug,
    router,
    selectedDeliveryAddressId,
    sharedLineItemId,
    track,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateToCustomization,
    navigateToLocationSearch,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductLineageKey: string;
  productSlug?: string;
  restaurantSlug?: string;
  selectedDeliveryAddressId?: string;
  sharedLineItemId?: string;
};
